"use client";

import { c as _c } from "react/compiler-runtime";
import { usePathname } from "next/navigation";
import { useEffect } from "react";
import { hasUsableAuthCookie } from "~/lib/auth/auth-cookie";
import { authClient } from "~/lib/auth/client";
import { isProtectedPath } from "~/lib/auth/protected-routes";
import { writeNavContext } from "~/lib/nav-context";
import { useNavAuthStore } from "~/store/navAuthStore";
import { api } from "~/trpc/react";
export type NavProfile = {
  firstName: string;
  lastName: string;
  email: string;
  isHost: boolean;
} | null;

/**
 * One query shared by every navbar consumer. React Query dedupes on the key, so
 * the shell, the actions slot, the sidebar and the user menu together still
 * cost one request.
 */
export function useNavUserQuery() {
  const $ = _c(2);
  const clientAuthenticated = useNavAuthStore(_temp);
  const t0 = clientAuthenticated === true;
  let t1;
  if ($[0] !== t0) {
    t1 = {
      enabled: t0,
      staleTime: 300000,
      retry: _temp2
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return api.user.getUser.useQuery(undefined, t1);
}

/**
 * Whether a non-host gets an Earnings entry in the account menus. Hosts reach
 * Earnings from the portal nav. A non-host gets it once there can be money to
 * show: a Stripe Connect account, or a referral reward. The rewards read is
 * skipped when the account alone already decides it. Shared by the user menu
 * and the mobile sidebar so both agree.
 */
function _temp2(count, err) {
  return err?.data?.code === "UNAUTHORIZED" ? false : count < 3;
}
function _temp(s) {
  return s.clientAuthenticated;
}
export function useDriverEarningsEntry(isHost) {
  const $ = _c(2);
  const {
    data: navUser
  } = useNavUserQuery();
  const hasStripeAccount = !!navUser?.stripe_connect_account_id;
  const t0 = !isHost && !!navUser && !hasStripeAccount;
  let t1;
  if ($[0] !== t0) {
    t1 = {
      enabled: t0,
      staleTime: 300000,
      retry: 1
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: referralRewards
  } = api.user.getReferralRewards.useQuery(undefined, t1);
  return !isHost && (hasStripeAccount || (referralRewards?.rewards.length ?? 0) > 0);
}

/**
 * Keeps `clientAuthenticated` in step with the auth cookie and guards protected
 * pages when it goes away. Mount exactly once, from the navbar shell.
 */
export function useNavAuthSync() {
  const $ = _c(10);
  const setClientAuthenticated = useNavAuthStore(_temp3);
  const utils = api.useUtils();
  const {
    error: clientUserError
  } = useNavUserQuery();
  let t0;
  if ($[0] !== setClientAuthenticated || $[1] !== utils.user.getUser) {
    t0 = () => {
      const syncAuthState = () => {
        const authenticated = hasUsableAuthCookie();
        setClientAuthenticated(authenticated);
        if (authenticated) {
          utils.user.getUser.invalidate();
        } else {
          utils.user.getUser.cancel();
        }
        return authenticated;
      };
      const reconcileAndGuard = () => {
        if (syncAuthState()) {
          return;
        }
        const {
          pathname,
          search
        } = window.location;
        if (isProtectedPath(pathname)) {
          window.location.replace(`/auth?next=${encodeURIComponent(pathname + search)}`);
        }
      };
      const unsubscribeAuth = authClient.onAuthChange(reconcileAndGuard);
      window.addEventListener("pageshow", syncAuthState);
      window.addEventListener("popstate", reconcileAndGuard);
      reconcileAndGuard();
      return () => {
        window.removeEventListener("pageshow", syncAuthState);
        window.removeEventListener("popstate", reconcileAndGuard);
        unsubscribeAuth();
      };
    };
    $[0] = setClientAuthenticated;
    $[1] = utils.user.getUser;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== setClientAuthenticated || $[4] !== utils) {
    t1 = [utils, setClientAuthenticated];
    $[3] = setClientAuthenticated;
    $[4] = utils;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  useEffect(t0, t1);
  let t2;
  if ($[6] !== clientUserError?.data?.code) {
    t2 = () => {
      if (clientUserError?.data?.code !== "UNAUTHORIZED") {
        return;
      }
      authClient.signOut().then(_temp4);
    };
    $[6] = clientUserError?.data?.code;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== clientUserError) {
    t3 = [clientUserError];
    $[8] = clientUserError;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  useEffect(t2, t3);
}

/**
 * The server-rendered profile reconciled against client auth state. The server
 * value is what makes a signed-in user's name correct on first paint; the
 * client override is what makes Back and sign-out correct afterwards.
 */
function _temp4() {
  const {
    pathname: pathname_0,
    search: search_0
  } = window.location;
  if (isProtectedPath(pathname_0)) {
    window.location.replace(`/auth?next=${encodeURIComponent(pathname_0 + search_0)}`);
  }
}
function _temp3(s) {
  return s.setClientAuthenticated;
}
export function useNavProfile(serverProfile) {
  const $ = _c(5);
  const clientAuthenticated = useNavAuthStore(_temp5);
  const {
    data: clientUser
  } = useNavUserQuery();
  if (clientAuthenticated === null) {
    return serverProfile;
  }
  if (!clientAuthenticated) {
    return null;
  }
  if (!clientUser) {
    return serverProfile;
  }
  const t0 = clientUser.email ?? "";
  let t1;
  if ($[0] !== clientUser.first_name || $[1] !== clientUser.is_host || $[2] !== clientUser.last_name || $[3] !== t0) {
    t1 = {
      firstName: clientUser.first_name,
      lastName: clientUser.last_name,
      email: t0,
      isHost: clientUser.is_host
    };
    $[0] = clientUser.first_name;
    $[1] = clientUser.is_host;
    $[2] = clientUser.last_name;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}

/**
 * Which side of the marketplace the chrome should show. The last-known value
 * lives in `navAuthStore` so every navbar region agrees on it even when one of
 * them unmounts. `persist` writes the cookie and must be set by exactly one
 * caller, or two consumers race on the same value.
 */
function _temp5(s) {
  return s.clientAuthenticated;
}
export function useNavContext(t0) {
  const $ = _c(6);
  const {
    profile,
    initialContext,
    persist: t1
  } = t0;
  const persist = t1 === undefined ? false : t1;
  const pathname = usePathname();
  const isHost = !!profile?.isHost;
  const onHostDashboard = pathname.startsWith("/host");
  const explicitContext = isHost && onHostDashboard ? "host" : pathname.startsWith("/driver") || pathname.startsWith("/search") || pathname === "/parking" ? "driver" : null;
  const lastContext = useNavAuthStore(_temp6);
  const setLastContext = useNavAuthStore(_temp7);
  let t2;
  let t3;
  if ($[0] !== explicitContext || $[1] !== isHost || $[2] !== persist || $[3] !== setLastContext) {
    t2 = () => {
      if (explicitContext !== null && isHost) {
        setLastContext(explicitContext);
        if (!persist) {
          return;
        }
        writeNavContext(explicitContext);
      }
    };
    t3 = [explicitContext, isHost, persist, setLastContext];
    $[0] = explicitContext;
    $[1] = isHost;
    $[2] = persist;
    $[3] = setLastContext;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  if (!isHost) {
    return "driver";
  }
  return explicitContext ?? lastContext ?? initialContext;
}
function _temp7(s_0) {
  return s_0.setLastContext;
}
function _temp6(s) {
  return s.lastContext;
}